import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, RefreshCw, AlertTriangle, Star, FlagOff } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { sortByCategoryOrder } from '../../../lib/categoryOrder';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';

interface DishAllergyCount { text: string; count: number; removable?: boolean; }
interface DishRow { dish_id: string; base_name: string; variant_label: string | null; removals?: string[]; allergy_reasons?: string[]; name: string; is_alternative: boolean; count: number; prepared: boolean; prepared_count: number | null; count_mismatch: boolean; allergies: DishAllergyCount[]; }
interface TableRow { table_number: string; is_vip?: boolean; guests: { id: string; name: string; checked_in_at: string; dishes: string[] }[]; dish_counts_by_category: Record<string, DishRow[]>; }
interface LiveCheckins { tables: TableRow[]; total_checked_in: number; }

// Regroupe les lignes plat+variante par plat de base, pour que le chef comprenne d'un coup d'œil "28 Lieu
// Noir, dont 3 sans sauce" plutôt qu'une liste plate de lignes indépendantes. Un plat sans aucune variante
// choisie à cette table reste affiché seul, sans sous-groupe inutile. Demande explicite d'Emeni (2026-08-23) :
// "il doit vraiment bien comprendre ce qu'il doit préparer de manière simple".
const groupDishesByBase = (dishes: DishRow[]) => {
    const order: string[] = [];
    const byBase = new Map<string, DishRow[]>();
    dishes.forEach((d) => {
        if (!byBase.has(d.base_name)) { byBase.set(d.base_name, []); order.push(d.base_name); }
        byBase.get(d.base_name)!.push(d);
    });
    return order.map((baseName) => {
        const rows = byBase.get(baseName)!.slice().sort((a, b) => (a.variant_label ? 1 : 0) - (b.variant_label ? 1 : 0));
        return { baseName, rows, total: rows.reduce((sum, r) => sum + r.count, 0) };
    });
};

export default function ModeTerrainPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const [data, setData] = React.useState<LiveCheckins | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [activeCategory, setActiveCategory] = React.useState<string | null>(null);
    const [togglingKey, setTogglingKey] = React.useState<string | null>(null);

    // LE DOSSIER DERRIERE LE SERVICE. Le Mode terrain ne connait qu'un evenement ; c'est le serveur qui dit
    // s'il existe un dossier de privatisation a cloturer, et ou il en est. Silencieux en cas d'echec : un
    // etablissement sans le module « Suivi privatisation » recoit un refus, et ce n'est pas une erreur —
    // simplement, il n'y a rien a cloturer, donc pas de bouton.
    const [dossier, setDossier] = React.useState<{ dossier_id: string; label: string; stage: string; stage_label: string } | null>(null);
    const [confirmation, setConfirmation] = React.useState(false);
    const [cloture, setCloture] = React.useState(false);
    const [erreurCloture, setErreurCloture] = React.useState('');

    React.useEffect(() => {
        if (!eventId) return;
        moduleApi.getDossierByEvent(eventId)
            .then((r: any) => setDossier(r?.dossier || null))
            .catch(() => setDossier(null));
    }, [eventId]);

    const terminerLeService = async () => {
        if (!eventId) return;
        setCloture(true);
        setErreurCloture('');
        try {
            await moduleApi.finishServiceForEvent(eventId);
            setConfirmation(false);
            const r: any = await moduleApi.getDossierByEvent(eventId).catch(() => null);
            setDossier(r?.dossier || null);
        } catch (e: any) {
            setErreurCloture(e?.message || "Le dossier n'a pas pu etre cloture.");
        } finally {
            setCloture(false);
        }
    };

    const load = React.useCallback(async () => {
        if (!eventId) return;
        try {
            const d = await moduleApi.getEvenementielCollecteLiveCheckins(eventId);
            setData(d);
        } catch (e) {
            // silencieux — on retentera au prochain cycle de polling
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    React.useEffect(() => {
        load();
        const interval = setInterval(load, 15000);
        return () => clearInterval(interval);
    }, [load]);

    const categories = React.useMemo(() => {
        if (!data) return [];
        const set = new Set<string>();
        data.tables.forEach((t) => Object.keys(t.dish_counts_by_category).forEach((c) => set.add(c)));
        return sortByCategoryOrder(Array.from(set));
    }, [data]);

    React.useEffect(() => {
        if (!activeCategory && categories.length > 0) setActiveCategory(categories[0]);
    }, [categories, activeCategory]);

    const handleToggle = async (tableNumber: string, category: string, dish: DishRow) => {
        if (!eventId) return;
        const key = `${tableNumber}|${category}|${dish.dish_id}`;
        setTogglingKey(key);
        setData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                tables: prev.tables.map((t) => t.table_number !== tableNumber ? t : {
                    ...t,
                    dish_counts_by_category: {
                        ...t.dish_counts_by_category,
                        [category]: t.dish_counts_by_category[category].map((d) => d.dish_id === dish.dish_id ? { ...d, prepared: !d.prepared } : d),
                    },
                }),
            };
        });
        try {
            await moduleApi.toggleEvenementielPrepStatus(eventId, { table_number: tableNumber, category, dish_id: dish.dish_id, count: dish.count });
        } catch (e) {
            load();
        } finally {
            setTogglingKey(null);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    const tablesForCategory = (data?.tables || []).filter((t) => (t.dish_counts_by_category[activeCategory || ''] || []).length > 0);

    return (
        <div className="space-y-4 max-w-4xl">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* Le Mode terrain est desormais son propre module : le retour se fait sur SA liste, et non
                    dans Menus & Convives — un chef n'a pas forcement acces au module commercial. */}
                <button onClick={() => navigate('/mode-terrain')} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content">
                    <ArrowLeft size={14} /> Retour aux services
                </button>
                <button onClick={load} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content">
                    <RefreshCw size={13} /> Actualiser — {data?.total_checked_in ?? 0} installé{(data?.total_checked_in ?? 0) > 1 ? 's' : ''}
                </button>
            </div>

            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-black text-content tracking-tight">Mode terrain</h1>
                    {dossier && <p className="text-xs text-content-muted mt-0.5">{dossier.label} — {dossier.stage_label}</p>}
                </div>
                {/* « Terminer le service » vit ICI et pas dans le suivi commercial : c'est le chef qui sait que
                    le service est fini, et il ne rouvrira pas un ecran commercial en fin de soiree pour le
                    dire. Demande d'Emeni (2026-08-26). La cloture n'envoie aucun email a personne. */}
                {dossier && dossier.stage !== 'cloturee' && (
                    <Button variant="primary" size="sm" onClick={() => { setErreurCloture(''); setConfirmation(true); }}>
                        <FlagOff size={13} /> Terminer le service
                    </Button>
                )}
                {dossier && dossier.stage === 'cloturee' && (
                    <span className="text-[11px] font-bold text-green-600 dark:text-green-400 flex items-center gap-1.5">
                        <Check size={13} /> Service terminé — dossier clôturé
                    </span>
                )}
            </div>

            {categories.length === 0 ? (
                <p className="text-sm text-content-muted">Aucun convive ne s'est encore déclaré assis à une table.</p>
            ) : (
                <>
                    <div className="flex gap-2 flex-wrap">
                        {categories.map((cat) => (
                            <button key={cat} onClick={() => setActiveCategory(cat)}
                                className={`px-4 py-2 rounded-xl text-sm font-black uppercase tracking-wide transition-colors ${activeCategory === cat ? 'bg-brand-600 text-brand-fg' : 'bg-surface-soft border border-border text-content-muted hover:text-content'}`}>
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {tablesForCategory.map((t) => {
                            const dishes = t.dish_counts_by_category[activeCategory || ''] || [];
                            const hasMismatch = dishes.some((d) => d.count_mismatch);
                            const allPrepared = !hasMismatch && dishes.every((d) => d.prepared);
                            return (
                                <div key={t.table_number} className={`rounded-2xl border p-3 space-y-2 ${hasMismatch ? 'border-red-500/40 bg-red-500/5' : t.is_vip ? 'border-amber-500/40 bg-amber-500/5' : allPrepared ? 'border-green-500/30 bg-green-500/5' : 'border-border bg-surface-soft'}`}>
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-lg font-black text-content flex items-center gap-1.5">
                                            {t.is_vip && <Star size={15} className="text-amber-500 shrink-0" fill="currentColor" />}
                                            Table {t.table_number}
                                        </h2>
                                        <span className="text-[11px] font-bold text-content-muted">{t.guests.length} pers.</span>
                                    </div>
                                    <div className="space-y-2">
                                        {groupDishesByBase(dishes).map((group) => (
                                            <div key={group.baseName} className="space-y-1">
                                                {group.rows.length > 1 && (
                                                    <p className="text-[11px] font-black text-content-muted uppercase tracking-wide">{group.baseName} — total {group.total}</p>
                                                )}
                                                {group.rows.map((d) => (
                                                    <div key={d.dish_id} className={group.rows.length > 1 ? 'ml-2.5' : ''}>
                                                        <button type="button" disabled={togglingKey === `${t.table_number}|${activeCategory}|${d.dish_id}`}
                                                            onClick={() => handleToggle(t.table_number, activeCategory || '', d)}
                                                            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-left transition-colors ${d.count_mismatch ? 'bg-red-500/10 border border-red-500/40 text-content' : d.prepared ? 'bg-green-500/15 text-green-600 dark:text-green-400' : (d.allergy_reasons || []).length ? 'bg-red-500/5 border border-red-500/40 text-red-600 dark:text-red-400' : 'bg-surface border border-border text-content hover:bg-surface-soft'}`}>
                                                            <span className={`text-sm font-bold flex items-center gap-1.5 min-w-0 ${d.prepared && !d.count_mismatch ? 'line-through opacity-70' : ''}`}>
                                                                {d.variant_label ? (
                                                                    <>
                                                                        {/* "SANS" pour un retrait demandé par le convive, "ALT" pour une
                                                                            déclinaison prévue par l'établissement : deux origines
                                                                            différentes, que le chef ne traite pas de la même façon. */}
                                                                        {/* Rouge quand le retrait vient d'une ALLERGIE : consigne impérative,
                                                                            à ne pas confondre avec une préférence du convive. Demande d'Emeni
                                                                            (2026-08-23). */}
                                                                        <span className={`shrink-0 text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${(d.allergy_reasons || []).length ? 'bg-red-600 text-white' : (d.removals || []).length ? 'bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-amber-500/20 text-amber-600 dark:text-amber-400'}`}>
                                                                            {(d.allergy_reasons || []).length ? 'ALLERGIE' : (d.removals || []).length ? 'SANS' : 'ALT'}
                                                                        </span>
                                                                        {/* Le nom du plat n'est repris que si le titre de groupe est masqué
                                                                            (une seule ligne) : sinon la carte affichait « sans crevettes »
                                                                            tout seul, sans dire de quel plat il s'agissait. Signalé par
                                                                            Emeni (2026-08-23). */}
                                                                        <span className="min-w-0">
                                                                            {group.rows.length === 1 && <span className="block">{d.base_name}</span>}
                                                                            <span className={group.rows.length === 1 ? 'block text-[11px] font-bold opacity-80' : ''}>{d.variant_label}</span>
                                                                            {/* La cuisine doit savoir DE QUOI il s'agit pour vérifier son
                                                                                assiette : "ALLERGIE" seul ne suffit pas. */}
                                                                            {(d.allergy_reasons || []).length > 0 && (
                                                                                <span className="block text-[10px] font-bold">allergie : {(d.allergy_reasons || []).join(', ')}</span>
                                                                            )}
                                                                        </span>
                                                                    </>
                                                                ) : d.base_name}
                                                            </span>
                                                            <span className="flex items-center gap-1.5 shrink-0">
                                                                {d.count_mismatch ? (
                                                                    <span className="text-[11px] font-black text-red-500 flex items-center gap-1">
                                                                        <AlertTriangle size={13} /> {d.prepared_count} → {d.count}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-base font-black">×{d.count}</span>
                                                                )}
                                                                {d.prepared && !d.count_mismatch && <Check size={16} />}
                                                            </span>
                                                        </button>
                                                        {/* Allergies des convives concernés par CE plat/variante précis, toujours
                                                            visibles (jamais masquées une fois l'assiette préparée) — demande
                                                            explicite d'Emeni (2026-08-23) : "on doit toujours voir les allergies
                                                            [...] des personnes concernées". */}
                                                        {/* Une consigne d'assiette se lit comme une ligne de plat : le libellé à
                                                            gauche, la quantité à droite, alignée sur celle du plat au-dessus.
                                                            "Salade de fruits ×2" puis "sans Kiwi ×1" — le chef voit d'un coup
                                                            combien d'assiettes partent en version modifiée. Un ingrédient
                                                            non retirable ne devrait jamais arriver ici (le convive ne peut pas
                                                            choisir le plat dans ce cas) : s'il apparaît quand même, c'est une
                                                            anomalie à traiter à la main, donc signalée comme telle. */}
                                                        {d.allergies.length > 0 && (
                                                            <div className="px-3 pt-1 space-y-0.5">
                                                                {d.allergies.map((a) => (
                                                                    <div key={a.text} className={`flex items-center justify-between gap-2 text-[11px] font-bold ${a.removable ? 'text-red-500' : 'text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg px-2 py-1'}`}>
                                                                        <span className="flex items-center gap-1 min-w-0">
                                                                            <AlertTriangle size={11} className="shrink-0" />
                                                                            {a.removable ? (
                                                                                <span className="truncate">sans {a.text}</span>
                                                                            ) : (
                                                                                <span className="truncate">ALLERGIE {a.text} — NON RETIRABLE, voir en salle</span>
                                                                            )}
                                                                        </span>
                                                                        <span className="shrink-0 font-black">×{a.count}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {confirmation && dossier && (
                <Modal open onClose={() => setConfirmation(false)} title="Terminer le service" icon={<FlagOff size={16} />} maxWidth="max-w-md"
                    footer={
                        <>
                            <Button variant="secondary" onClick={() => setConfirmation(false)} disabled={cloture}>Annuler</Button>
                            <Button variant="primary" onClick={terminerLeService} disabled={cloture}>
                                {cloture ? <Loader2 size={13} className="animate-spin" /> : null} Terminer le service
                            </Button>
                        </>
                    }>
                    <div className="space-y-3">
                        <p className="text-xs text-content-muted leading-relaxed">
                            Le dossier <span className="font-bold text-content">{dossier.label}</span> passera de
                            « {dossier.stage_label} » à <span className="font-bold text-content">Clôturée</span>.
                            C'est la fin du parcours commercial de cette privatisation.
                        </p>
                        <p className="text-[11px] text-content-muted leading-relaxed p-2.5 rounded-xl bg-surface-soft border border-border">
                            Aucun email n'est envoyé, ni au client ni aux convives. Les étapes qui n'auraient pas
                            été renseignées à temps seront notées comme passées dans l'historique du dossier,
                            jamais comme accomplies.
                        </p>
                        {erreurCloture && <p className="text-xs text-red-500 font-semibold">{erreurCloture}</p>}
                    </div>
                </Modal>
            )}
        </div>
    );
}
